import React, { useState, useEffect } from 'react';
import { chatStyles } from '../../styles/chat.styles';
import { getModelDisplayName, getModelIcon as configGetModelIcon } from '../../config/models';
import ImageLoadingAnimation from './ImageLoadingAnimation';
import { Theme, MessageWithCitations, themeColors } from './types';

interface GeneratedImageMessageProps {
  message: MessageWithCitations;
  theme: Theme;
  retrieveFile: (filename: string) => Promise<File | undefined>;
}

const GeneratedImageMessage: React.FC<GeneratedImageMessageProps> = ({ message, theme, retrieveFile }) => {
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);
  
  useEffect(() => {
    let isMounted = true;
    
    const loadImage = async () => {
      // Special case for initial loading state
      if (message.fileUrl === 'generating') {
        setIsLoading(true);
        setError(false);
        return;
      }

      if (!message.fileUrl) {
        console.error('GeneratedImageMessage: No fileUrl provided in message:', message);
        setError(true);
        setIsLoading(false);
        return;
      }
      
      try {
        setIsLoading(true);
        console.log('GeneratedImageMessage: Attempting to retrieve image:', message.fileUrl);
        const file = await retrieveFile(message.fileUrl);
        console.log('GeneratedImageMessage: Retrieved file:', file ? {
          name: file.name,
          type: file.type,
          size: file.size
        } : 'Not found');
        
        if (file && isMounted) {
          const url = URL.createObjectURL(file);
          console.log('GeneratedImageMessage: Created object URL:', url);
          setImageUrl(url);
          setError(false);
        } else if (isMounted) {
          console.error('GeneratedImageMessage: Image file not found in IndexedDB:', message.fileUrl);
          setError(true);
        }
      } catch (err) {
        console.error('GeneratedImageMessage: Error loading image:', err);
        if (isMounted) setError(true);
      } finally {
        if (isMounted && message.fileUrl !== 'generating') {
          setIsLoading(false);
        }
      }
    };
    
    loadImage();
    
    return () => {
      isMounted = false;
      if (imageUrl) {
        URL.revokeObjectURL(imageUrl);
      }
    };
  }, [message.fileUrl, retrieveFile]);
  
  const messageStyle = message.role === 'user' 
    ? { backgroundColor: themeColors[theme].messageBackground.user } 
    : { backgroundColor: themeColors[theme].messageBackground.assistant };
  
  return (
    <div style={{
      padding: '20px',
      ...messageStyle,
      borderBottom: `1px solid ${themeColors[theme].border}`
    }}>
      {/* Header with model info */}
      <div style={{ 
        marginBottom: '16px',
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        fontSize: '12px',
        color: themeColors[theme].secondaryText
      }}>
        <img 
          src={configGetModelIcon(message.model, theme)} 
          alt="" 
          style={{ width: '14px', height: '14px', objectFit: 'contain' }} 
        />
        <span>{getModelDisplayName(message.model)}</span>
      </div>
      
      {/* Image container */}
      <div style={chatStyles.generatedImageContainer}>
        {/* Header */}
        <div style={chatStyles.generatedImageHeader}>
          <span>Generated Image</span>
          {imageUrl && !isLoading && !error && (
            <button 
              onClick={() => {
                if (imageUrl) {
                  const a = document.createElement('a');
                  a.href = imageUrl;
                  a.download = message.fileUrl || 'generated-image.png';
                  document.body.appendChild(a);
                  a.click();
                  document.body.removeChild(a);
                }
              }}
              style={{
                background: 'none',
                border: 'none',
                color: '#10A37F',
                cursor: 'pointer',
                fontSize: '12px',
                display: 'flex',
                alignItems: 'center',
                gap: '4px'
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 15v4c0 1.1-.9 2-2 2H5a2 2 0 0 1-2-2v-4"/>
                <path d="M7 10l5 5 5-5"/>
                <path d="M12 15V3"/>
              </svg>
              Download
            </button>
          )}
        </div>
        
        {/* Image content */}
        <div style={{ padding: '0' }}>
          {isLoading ? (
            <ImageLoadingAnimation 
              theme={theme} 
              message={message.fileUrl === 'generating' ? "Generating image..." : "Loading image..."} 
            />
          ) : error ? (
            <div style={{
              textAlign: 'center',
              padding: '40px 20px',
              color: theme === 'light' ? '#991B1B' : '#FCA5A5',
              backgroundColor: theme === 'light' ? '#FEF2F2' : '#2D1F1F',
              borderRadius: '8px'
            }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ margin: '0 auto 16px', display: 'block' }}>
                <circle cx="12" cy="12" r="10"/>
                <line x1="12" y1="8" x2="12" y2="12"/>
                <line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
              <div>Failed to load image</div>
              <div style={{ fontSize: '12px', marginTop: '8px', opacity: 0.8 }}>
                Filename: {message.fileUrl}
              </div>
            </div>
          ) : (
            <img 
              src={imageUrl!} 
              alt="AI generated image" 
              style={chatStyles.generatedImage}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default GeneratedImageMessage;